/*
 * Industry content — `en` (international market, source locale).
 *
 * This is the international variant of every industry (the old per-field `*Intl`
 * copy, resolved): generic time-registration framing, no belgium-specific pains/
 * features, and `compliance.slug` / `relatedArticles` pointing only at articles
 * that exist under `/en/` (the 4 market-agnostic posts). `nl.ts` carries the
 * belgium variant. Resolved by `industriesFor("en")` (also the SOURCE_LOCALE
 * fallback). Pure data (NFR10).
 */
import type { Industry } from "./types";

export const INDUSTRIES_EN: Industry[] = [
  {
    slug: "construction",
    name: "Construction",
    headline: "Time tracking for construction teams — location-verified check-in, project hours and budget control, in one app",
    intro: "Crews spread across sites, hours scribbled on paper, subcontractor hours impossible to reconcile. Done-it gives every site a watertight digital timesheet that feeds your payroll in one click.",
    pains: [
      {
        title: "No reliable record of who worked where",
        body: "With multiple active sites and rotating crews, manual timesheets are wrong by design. Workers round up, forget entries or fill them in days later — leaving you with payroll figures you can't defend.",
      },
      {
        title: "Budget overruns surface too late",
        body: "By the time you notice a site has burned through its planned hours, the margin is already gone. There's no live countdown — just a nasty surprise at invoice time.",
      },
      {
        title: "Payroll prep eats hours every month",
        body: "Collecting timesheets, reconciling them against projects and preparing the payroll export takes days. Every entry is a potential error.",
      },
    ],
    features: [
      {
        title: "Location-verified check-in per site",
        body: "One tap checks the worker in, location-verified, and links the hours to the right project. No card readers, no terminals — just the smartphone your crew already carries.",
      },
      {
        title: "Live remaining-hours per project",
        body: "Set planned hours as a budget when you open the project. Workers see a countdown of what's left; you see it live in your dashboard. Budget overruns are visible before they happen.",
      },
      {
        title: "Subcontractor management and presence",
        body: "Add subcontractors to a project, assign them to check-in and track their hours alongside your own crew — in the same overview.",
      },
      {
        title: "One-click payroll export",
        body: "All registered hours — per worker, per project — export to a clean spreadsheet for your payroll provider. No manual preparation, no errors.",
      },
    ],
    compliance: {
      label: "Digital time registration",
      title: "Location-verified check-in built for construction sites",
      body: "Done-it provides location-verified check-in, per-project time tracking and a clean payroll export for construction teams. For teams operating on Belgian sites, it also feeds check-in at work automatically.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Crew arrives on site", body: "Each worker taps once to check in. GPS confirms the location, timestamps the entry and links hours to the project. No paper, no terminal." },
      { step: "You see everything live", body: "Hours, remaining budget, subcontractor presence and any extra work reported from site — all in your dashboard, in real time." },
      { step: "Month-end in minutes", body: "Export all hours per worker and per project. Send to your payroll provider. Done-it generates clean, audit-ready reports automatically." },
    ],
    stats: [
      { value: "1 tap", label: "to check in on any site — no terminal, no paper" },
      { value: "1 click", label: "payroll export ready for your provider" },
      { value: "Live", label: "remaining-hours view per project and site" },
    ],
    quote: {
      text: "Our crew checks in themselves on site. At month-end, payroll just adds up — we stopped chasing timesheets altogether.",
      who: "Owner, construction company · 14 employees",
      result: "5 hours a week saved on admin",
    },
    relatedSlugs: ["installation", "maintenance", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "cleaning",
    name: "Cleaning",
    headline: "Time tracking for cleaning companies — location-verified check-in, attendance proof and payroll in one app",
    intro: "Cleaners move between locations all day. Done-it tracks who is where in real time — location-verified check-in at each site, automatic break deduction and a clean payroll export at month-end.",
    pains: [
      {
        title: "Proving presence on every location",
        body: "Clients and inspectors expect watertight attendance records. Manual sheets can be challenged; location-verified digital check-ins cannot.",
      },
      {
        title: "Teams spread across many locations",
        body: "With cleaners moving between sites throughout the day, you have no live view of who is working where — until someone calls.",
      },
      {
        title: "Payroll errors from rounded hours",
        body: "Without accurate in/out times, payroll is reconstructed from memory. Hours get rounded up, costs inflate and disputes follow.",
      },
    ],
    features: [
      {
        title: "Real-time check-in and out",
        body: "Workers tap in and out on their phone at each location, location-verified; time is exact, with break logging.",
        plan: "Time Basic",
      },
      {
        title: "Automatic break deduction",
        body: "Mandatory breaks are subtracted from the working day automatically — no manual maths, no disputes.",
        plan: "Time Basic",
      },
      {
        title: "Multi-location live overview",
        body: "See which cleaners are at which locations right now. Spot gaps before clients do.",
      },
      {
        title: "Absence registration with digital sick notes",
        body: "Workers upload sick certificates and absence documents directly from the app. Create the absence types your team actually uses.",
      },
      {
        title: "One-click payroll export",
        body: "Exact timestamps flow straight into a payroll-ready export for your provider. No transcription, no errors.",
      },
    ],
    compliance: {
      label: "Location-verified attendance",
      title: "Real-time attendance across every cleaning location",
      body: "Done-it gives cleaning teams location-verified check-in at every location, automatic break deduction and a live view of who is on which site right now.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Cleaner arrives at location", body: "One tap checks them in. GPS logs the site and timestamps the entry. Break registration is built into the app." },
      { step: "You see every location live", body: "Who is at which site right now — no phone calls, no guesswork. Spot coverage gaps before clients do." },
      { step: "Payroll is one click", body: "All attendance data — exact times, locations and breaks — exports cleanly for your payroll provider." },
    ],
    stats: [
      { value: "1 tap", label: "to check in at any location — no terminal needed" },
      { value: "Live", label: "view of who is on which site right now" },
      { value: "0", label: "double entries — hours flow straight to payroll" },
    ],
    quote: {
      text: "We can finally prove who cleaned where and when. No more attendance sheets, no more client disputes — GPS takes care of it.",
      who: "Manager, cleaning company · 9 employees",
      result: "Zero rounding-up of hours",
    },
    relatedSlugs: ["maintenance", "field-service", "construction"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "installation",
    name: "Installation",
    headline: "Time tracking for installation teams — project hours, navigation and budget control in one app",
    intro: "Send your fitters to the right address with everything they need, link every worked hour to the right job automatically, and see remaining budget before the site closes. Done-it is the simplest way to keep installation projects on time and on cost.",
    pains: [
      {
        title: "Job info doesn't reach the field in time",
        body: "Crews arrive at a site without the address, contact person or scope details. That first phone call is ten minutes of billable time lost before a single hour is worked.",
      },
      {
        title: "Hours aren't linked to the right project",
        body: "When workers fill in their hours manually at day's end — if they fill them in at all — hours land in the wrong job, or disappear entirely. Invoices don't match reality.",
      },
      {
        title: "Budget overruns spotted too late",
        body: "Without a live countdown of remaining hours per project, you find out a job ran over when you close the invoice — not while you can still react.",
      },
      {
        title: "Subcontractor hours hard to track",
        body: "Subcontractors working on the same site use different systems, fill in hours separately or not at all. Reconciling at month-end is a slow, error-prone process.",
      },
    ],
    features: [
      {
        title: "Project info shared directly to the app",
        body: "Workers open the project on their phone: address, planned hours, contact person and uploaded files (plans, instructions, PDFs). Everything they need is there before they arrive.",
        plan: "Project Basic",
      },
      {
        title: "One-button navigation to site",
        body: "Tap once and the phone navigates to the project address — built into the app, no separate map app needed.",
      },
      {
        title: "Per-project remaining-hours countdown",
        body: "Set planned hours when you open the project. Workers see how many hours are left; you see the live status in your dashboard. Overruns are visible before they happen.",
        plan: "Project Basic",
      },
      {
        title: "Worker check-in per project",
        body: "Each worker checks in at the right project. You always know who is on which site right now.",
        plan: "Project Basic",
      },
      {
        title: "Extra work & material logging",
        body: "When scope creeps on site, workers log extra work and materials in real time — with text and photos — so nothing gets billed from memory.",
        plan: "Project Basic",
      },
    ],
    compliance: {
      label: "Digital time registration",
      title: "Per-project location-verified check-in for installation teams",
      body: "Done-it gives installation teams location-verified check-in per project, a live remaining-hours countdown and automatic payroll export — covering any local time-registration requirement. For teams working on Belgian construction sites, it also integrates with CheckIn@Work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Worker opens the project on their phone", body: "Address, contact, scope and file uploads are right there. They navigate with one tap — no calls to the office for directions." },
      { step: "Hours log automatically to the right job", body: "Check-in ties every worked minute to the correct project. Remaining budget updates in real time." },
      { step: "Extra work is captured on site", body: "Scope changes, extra materials and delays are logged with text and photos the moment they happen — ready for the invoice." },
    ],
    stats: [
      { value: "Real-time", label: "remaining-hours view per project" },
      { value: "0", label: "office calls for site addresses" },
      { value: "1 click", label: "navigate to any project address from the app" },
    ],
    quote: {
      text: "Hours land on the right project automatically. Job costing is suddenly easy — we can see the real margin before we close a job.",
      who: "Owner, installation company · 6 employees",
      result: "Real cost per job, live",
    },
    relatedSlugs: ["construction", "electricians", "hvac"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "field-service",
    name: "Field service",
    headline: "Time tracking for field service teams — mileage, expenses and hours from the road, all in one app",
    intro: "Technicians visiting multiple clients a day, receipts in gloveboxes, mileage logged from memory. Done-it captures hours, kilometres and expenses in the field — attached to the right job the moment they happen.",
    pains: [
      {
        title: "Mileage logged days later — if at all",
        body: "Asking technicians to reconstruct their routes at week's end means inaccurate reimbursements, disputes with clients and a growing pile of unclaimed kilometres.",
      },
      {
        title: "Expenses on paper receipts that disappear",
        body: "A receipt in a glovebox is a cost that may never make it into an invoice. The real cost per job is always an undercount.",
      },
      {
        title: "No real-time view of where the team is",
        body: "Without live check-ins, you only find out where a technician is when something goes wrong and someone calls.",
      },
      {
        title: "Hard to see the actual margin per job",
        body: "When labour, travel and expenses are tracked in separate systems — or not tracked at all — there's no single cost figure per job, ever.",
      },
    ],
    features: [
      {
        title: "Automatic mileage capture (Trax-it)",
        body: "GPS logs departure, arrival and exact distance per trip. Configure transport types (own car, company vehicle, carpool) with their own reimbursement rates. No manual logbook.",
        plan: "Add-on",
      },
      {
        title: "Expense management on site",
        body: "Workers snap a receipt in the field; it's attached to the job and ready for reimbursement — not chased at month-end.",
        plan: "Time Basic",
      },
      {
        title: "Project-linked cost control",
        body: "Hours, mileage and expenses roll up against the job in real time. The cost per job is visible while the job is still open.",
        plan: "Project Basic",
      },
      {
        title: "Location-verified check-in and live team overview",
        body: "Each technician checks in at the client site. You see who is where, right now — no calls, no guesswork.",
      },
      {
        title: "One-click payroll export",
        body: "Hours and kilometre reimbursements export cleanly for your payroll provider — with your configured mileage rates applied automatically.",
      },
    ],
    compliance: {
      label: "Digital time & mileage tracking",
      title: "GPS hours and automatic mileage for field service teams",
      body: "Done-it captures mileage automatically per trip, applies your configured reimbursement rates and logs every kilometre to the right client or project. The EU Tyco ruling means home-to-first-client travel counts as working time — Done-it tracks that too.",
      slug: "how-to-track-mileage-for-field-teams",
    },
    workflow: [
      { step: "Technician leaves for the first call", body: "Check-in starts the trip log. Kilometres are captured automatically from departure to arrival — no manual entry." },
      { step: "At the job site", body: "Hours link to the right project. Extra work, materials and receipts are logged in the app the moment they happen." },
      { step: "Month-end without the chaos", body: "Every hour, kilometre and expense is already attached to the correct job. Export to payroll and invoice with real cost figures." },
    ],
    stats: [
      { value: "Automatic", label: "mileage capture — no manual logbook" },
      { value: "Live", label: "cost per job: hours + travel + expenses" },
      { value: "0", label: "kilometres lost — every trip captured automatically" },
    ],
    quote: {
      text: "Mileage used to be a monthly headache. Now it logs itself — every kilometre is attached to the right client before we even get back to the office.",
      who: "Operations manager, field service company · 11 employees",
      result: "Accurate cost per job, every time",
    },
    relatedSlugs: ["hvac", "maintenance", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "hvac",
    name: "HVAC",
    headline: "Time tracking for HVAC technicians — job costing, parts and travel in one app",
    intro: "Every service call has three costs: labour, parts and travel. Done-it captures all three in the field and rolls them up against the right job — so you know your margin before you close the invoice.",
    pains: [
      {
        title: "Service calls are hard to cost accurately",
        body: "Labour, parts and travel live in different systems — or different pockets. The real cost of a call-out only becomes clear long after the technician has left the site.",
      },
      {
        title: "No proof of service time",
        body: "When a client disputes how long a repair took, a paper timesheet is easy to challenge. GPS-confirmed check-in and check-out timestamps are not.",
      },
      {
        title: "Maintenance contracts erode margin silently",
        body: "On recurring maintenance contracts, small overruns accumulate across many visits. Without a live hours counter, you only find out the contract is underwater at renewal.",
      },
      {
        title: "Technicians forget to log breaks",
        body: "Manual time entries miss breaks, leading to overpayment and payroll that doesn't match reality.",
      },
    ],
    features: [
      {
        title: "Project-based time tracking",
        body: "Every service call or maintenance contract is a project. Hours, travel and materials roll up in real time — giving you live margin visibility.",
        plan: "Project Basic",
      },
      {
        title: "Extra work and materials, logged on site",
        body: "When a job grows, technicians log extra work and parts with text and photos before they leave. Nothing gets billed from memory.",
        plan: "Project Basic",
      },
      {
        title: "Mileage and transport costs",
        body: "Kilometres per call-out are logged automatically and costed against the job. Configure reimbursement rates per vehicle type.",
        plan: "Add-on",
      },
      {
        title: "Automatic break deduction",
        body: "Mandatory breaks are subtracted automatically from total time — no disputes, no payroll errors.",
      },
      {
        title: "Location-verified check-in and proof of presence",
        body: "Timestamped, GPS-confirmed check-in at each site. Undisputable proof of when the technician arrived and left.",
      },
    ],
    compliance: {
      label: "Digital time & cost tracking",
      title: "Full job costing for HVAC service calls",
      body: "Done-it gives HVAC teams location-verified check-in per service call, live cost visibility per job (labour + travel + parts) and a clean payroll export — meeting any local time-registration requirement without extra hardware.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Technician checks in at the site", body: "GPS timestamps the arrival and links hours to the service call or maintenance contract. No paper, no terminal." },
      { step: "Extra work is captured on the spot", body: "Parts used, extra hours and scope changes are logged in the app with photos — before leaving the site." },
      { step: "Job cost is visible immediately", body: "Labour, travel and materials add up against the project in real time. You see the margin before you issue the invoice." },
    ],
    stats: [
      { value: "3", label: "cost types captured per call: labour, travel, parts" },
      { value: "Live", label: "margin visibility per service contract" },
      { value: "0", label: "surprises at invoice time — costs visible in real time" },
    ],
    quote: {
      text: "We finally know what each service contract actually costs us. The margin surprises stopped the day we started using Done-it.",
      who: "Owner, HVAC company · 7 technicians",
      result: "Real cost per service call, live",
    },
    relatedSlugs: ["installation", "electricians", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "time-tracking-for-field-teams-complete-guide",
    ],
  },

  {
    slug: "electricians",
    name: "Electricians",
    headline: "Time tracking for electricians — per-project hours, budget control and navigation in one app",
    intro: "Per-project hours, a live countdown of remaining budget and one-button navigation to site — built for small electrical teams that need project control without the admin overhead.",
    pains: [
      {
        title: "Hours aren't tied to the right project",
        body: "When electricians work across multiple jobs in a week, manually allocating hours at day's end leads to misattributed costs, billing disputes and payroll you can't explain.",
      },
      {
        title: "Budget overruns spotted too late",
        body: "Without a live view of hours consumed vs. hours budgeted, you find out a project ran over when you raise the invoice — not when you could still react.",
      },
      {
        title: "Time wasted finding the site",
        body: "Addresses typed into a separate app, calls to the office for directions, late arrivals at client sites. Every wasted minute is a billable minute lost.",
      },
      {
        title: "Subcontractor hours hard to reconcile",
        body: "On larger installs, subcontractor electricians track their own hours separately. Getting a combined view of labour costs at the end of a project is a manual, error-prone job.",
      },
    ],
    features: [
      {
        title: "Project hours with live remaining-budget",
        body: "Every worked minute is linked to the right project automatically. A live countdown shows how many hours remain against the budget — for you and for the worker on site.",
        plan: "Project Basic",
      },
      {
        title: "One-button navigation to site",
        body: "The project address is in the app. One tap opens navigation — no searching, no separate map app, no calls to the office.",
      },
      {
        title: "Worker check-in per project",
        body: "Each electrician checks in at the right job. You always know who's on which site right now.",
        plan: "Project Basic",
      },
      {
        title: "Extra work logged on site",
        body: "When scope changes — an unexpected circuit, additional materials — it's logged in the app with photos, before the crew leaves.",
        plan: "Project Basic",
      },
      {
        title: "Overtime tracking with flexible compensation",
        body: "Overtime is logged the moment it happens. Workers choose to have it paid out or converted to time off.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Per-project location-verified check-in",
      title: "Live budget control and location-verified check-in for electrical teams",
      body: "Done-it gives electricians per-project location-verified check-in, a live remaining-hours budget and payroll export — satisfying any local time-registration requirement. For teams on Belgian construction sites, it also integrates with CheckIn@Work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Electrician navigates to the job", body: "Address and scope are already in the app. One tap opens navigation — no calls, no confusion." },
      { step: "Hours link to the right project automatically", body: "Check-in at the site ties every worked minute to the correct project. The remaining-hours counter updates live." },
      { step: "Scope changes stay on the record", body: "Extra work and materials are logged on site, before leaving. The invoice reflects what actually happened." },
    ],
    stats: [
      { value: "Live", label: "remaining-hours view per project" },
      { value: "0", label: "office calls for site addresses" },
      { value: "1 tap", label: "to check in at any site — no terminal needed" },
    ],
    quote: {
      text: "We know in real time if a project is running over budget. That alone has saved us from two bad surprises this year.",
      who: "Owner, electrical company · 5 electricians",
      result: "Budget overruns caught before they happen",
    },
    relatedSlugs: ["installation", "hvac", "plumbing"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "plumbing",
    name: "Plumbing",
    headline: "Time tracking for plumbing teams — fast check-in, accurate payroll and proof of presence for every call-out",
    intro: "Emergency call-outs, multiple jobs per day, hours filled in from memory at week's end — or not at all. Done-it makes it a one-tap job at each site and a one-click payroll at month-end.",
    pains: [
      {
        title: "Call-out hours reconstructed from memory",
        body: "With several jobs in a day, plumbers rarely have time to fill in accurate timesheets. Hours are rounded or forgotten, and payroll is an estimate at best.",
      },
      {
        title: "No proof of time on site",
        body: "When a client disputes how long a repair took, there's no objective record. A GPS-confirmed digital check-in is easy to produce — a paper timesheet is not.",
      },
      {
        title: "Mileage between jobs untracked",
        body: "Travel between call-outs adds up over the week and month, but without automatic capture it's rarely invoiced or reimbursed correctly.",
      },
      {
        title: "Payroll preparation is slow",
        body: "At month-end, chasing timesheets, correcting errors and building the payroll export takes time your admin team shouldn't be spending.",
      },
    ],
    features: [
      {
        title: "One-tap location-verified check-in at each call-out",
        body: "Arrival and departure logged in one tap, with location verification of the location. Fast enough for emergency calls; precise enough for disputes.",
        plan: "Time Basic",
      },
      {
        title: "Automatic break deduction",
        body: "Mandatory breaks are subtracted from total time automatically — nothing to fill in, no payroll errors.",
        plan: "Time Basic",
      },
      {
        title: "Mileage captured per trip",
        body: "Configure transport types and reimbursement rates. Kilometres log automatically and link to the correct client or project.",
        plan: "Add-on",
      },
      {
        title: "Absence and sick-note management",
        body: "Workers submit sick notes digitally from the app. Create the absence types your team actually uses, including weather delays.",
      },
      {
        title: "One-click payroll export",
        body: "All hours and kilometre data exports cleanly for your payroll provider — no manual preparation, no Excel.",
        plan: "Time Basic",
      },
    ],
    compliance: {
      label: "Digital time registration",
      title: "Fast location-verified check-in for every call-out",
      body: "Done-it covers any local time-registration requirement for plumbing teams — one-tap location-verified check-in per call-out, automatic break deduction and a clean payroll export. No paper timesheets, no manual reconstruction.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Plumber arrives at the call-out", body: "One tap checks in. GPS logs the location and time. Fast enough for an emergency job." },
      { step: "Departure tapped on the way out", body: "Check-out closes the time entry. Mileage to the next job starts logging automatically." },
      { step: "Month-end is one click", body: "All call-outs, hours and kilometres export cleanly. Payroll prep goes from a day to minutes." },
    ],
    stats: [
      { value: "1 tap", label: "to check in — fast enough for emergency calls" },
      { value: "1 click", label: "payroll export to your provider" },
      { value: "0", label: "hours reconstructed from memory at month-end" },
    ],
    quote: {
      text: "We used to spend a morning at month-end reconstructing hours. Now we just export and send. The payroll provider gets it clean.",
      who: "Owner, plumbing company · 4 plumbers",
      result: "Payroll prep down from hours to minutes",
    },
    relatedSlugs: ["hvac", "electricians", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "landscaping",
    name: "Landscaping",
    headline: "Time tracking for landscaping companies — seasonal crews, multiple sites and mileage in one overview",
    intro: "Peak season means many sites, changing crews and a lot of kilometres. Done-it keeps every hour, every absence and every trip organised — so you can focus on the work, not the paperwork.",
    pains: [
      {
        title: "Seasonal crews are complex to manage",
        body: "Temporary workers, shifting schedules and varying team sizes make accurate hour tracking almost impossible with paper. By the time you reconcile at month-end, something is always missing.",
      },
      {
        title: "Hours spread across many sites",
        body: "A crew that visits three or four sites per day generates hours that need to be split correctly. Manual entry leads to misattribution and contested payroll.",
      },
      {
        title: "Weather stoppages are hard to document",
        body: "Bad-weather standstill is a real event in landscaping. Without a digital record, documenting stoppages and adjusting pay is slow and error-prone.",
      },
      {
        title: "Mileage between sites goes untracked",
        body: "Company vans, personal vehicles and carpools crisscross between sites all day. Without automatic capture, those kilometres never make it into the cost picture.",
      },
    ],
    features: [
      {
        title: "Multi-site crew overview",
        body: "See all crew, all sites in real time. Know who is working where, right now — not at month-end.",
      },
      {
        title: "Shift scheduling for seasonal teams",
        body: "Plan who works where, handle last-minute changes and push the schedule to the team's phones — without a phone call to each person.",
        plan: "Time Pro",
      },
      {
        title: "Custom absence types",
        body: "Create custom absence types for your sector: seasonal weather stoppages, parental leave, sick days. Workers upload documentation digitally from the app.",
      },
      {
        title: "Mileage with carpool tracking",
        body: "Configure transport types (company van, personal car, carpool driver vs. passenger) with their own rates. Kilometres log automatically per trip.",
        plan: "Add-on",
      },
      {
        title: "Overtime tracking with leave conversion",
        body: "Peak-season overtime accumulates fast. Each worker can choose to be paid out or bank the hours as time off — tracked transparently.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Digital time registration",
      title: "Location-verified check-in and absence tracking for seasonal landscaping teams",
      body: "Done-it gives landscaping teams digital time registration across all sites and seasonal staff — location-verified check-in, custom absence types for weather stoppages and a clean payroll export satisfying any local requirement.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Crew checks in at the first site", body: "GPS logs arrival. Hours automatically split to the right location and project — no manual allocation at day's end." },
      { step: "Weather stops work", body: "Foreman registers the stoppage from the app — a clean digital record, no extra paperwork." },
      { step: "Month-end: all sites, all crew, one export", body: "Hours, absences and mileage across every site and every crew member export cleanly for payroll." },
    ],
    stats: [
      { value: "Automatic", label: "mileage capture — no more logbook" },
      { value: "Live", label: "crew overview across all sites" },
      { value: "1 click", label: "payroll export for all sites and all crew" },
    ],
    quote: {
      text: "Peak season used to mean payroll chaos. Now our foreman checks the app, everyone is accounted for, and I export the hours on the last day of the month.",
      who: "Owner, landscaping company · 12 seasonal workers",
      result: "No more end-of-month payroll reconstruction",
    },
    relatedSlugs: ["maintenance", "construction", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "maintenance",
    name: "Maintenance",
    headline: "Time tracking for maintenance teams — recurring site visits, cost per contract and attendance proof in one app",
    intro: "Recurring maintenance contracts live or die on cost visibility. Done-it logs every visit, tracks hours against the contract budget and gives you — and your clients — proof of presence for every call.",
    pains: [
      {
        title: "No cost visibility per maintenance contract",
        body: "When hours for recurring visits aren't tracked against the specific contract, you don't know if a client is profitable until the year-end review — by which point it's too late to reprice.",
      },
      {
        title: "Clients expect proof of what was done",
        body: "Facilities managers and property owners increasingly want attendance records: who came, when they arrived, what they did. A paper log presented weeks later doesn't satisfy SLA requirements.",
      },
      {
        title: "Recurring visits hard to schedule and log",
        body: "Rotating shifts across many recurring sites — with staff who cover for each other — create a scheduling and hour-tracking puzzle that manual processes can't reliably solve.",
      },
      {
        title: "Weather stoppages affect outdoor maintenance too",
        body: "Groundskeeping, facade maintenance and outdoor facilities work all face weather-related stoppages. Documenting them correctly requires a digital trail.",
      },
    ],
    features: [
      {
        title: "Contract-based cost tracking",
        body: "Each maintenance contract is a project. Hours log automatically against it, giving you a live cost figure and remaining-hours countdown for each client.",
        plan: "Project Basic",
      },
      {
        title: "GPS-confirmed attendance proof",
        body: "Timestamped, location-verified check-ins are an undisputable record of who was on site, when, and for how long — ready to share with clients on request.",
      },
      {
        title: "Updates with photos from site",
        body: "Workers report issues, completed tasks or extra work directly from the app — with text and photos. Clients get a real-time record; you have the documentation for billing.",
        plan: "Project Basic",
      },
      {
        title: "Recurring shift scheduling",
        body: "Plan recurring visits across all contracts, assign workers and handle substitutions — without a phone call to each person.",
        plan: "Time Pro",
      },
      {
        title: "Absence types for outdoor maintenance",
        body: "Configure weather stoppages and other absence types. Workers register them from the app; documentation uploads digitally.",
      },
    ],
    compliance: {
      label: "Contract-based time tracking",
      title: "Live cost visibility per maintenance contract",
      body: "Done-it gives maintenance teams GPS-confirmed attendance proof per contract, custom absence types and a live cost view per recurring client — satisfying any local time-registration requirement without extra hardware.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Worker arrives at the maintenance site", body: "Location-verified check-in logs the arrival time and links hours to the correct contract. Attendance proof is created automatically." },
      { step: "Tasks completed, issues logged", body: "Work completed and any issues are reported from the app with photos. Clients can see exactly what was done." },
      { step: "Cost per contract visible immediately", body: "Hours accumulate against the contract budget in real time. You know the margin before the contract renews." },
    ],
    stats: [
      { value: "Live", label: "cost per maintenance contract" },
      { value: "GPS", label: "attendance proof ready for clients on demand" },
      { value: "0", label: "contract renewals without knowing your margin" },
    ],
    quote: {
      text: "We can tell a facilities manager exactly who was on site and when, in two minutes. That level of proof changed how clients see us.",
      who: "Operations manager, maintenance company · 8 workers",
      result: "Client disputes down to zero",
    },
    relatedSlugs: ["cleaning", "landscaping", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
  {
    slug: "retail",
    name: "Shops & retail",
    headline: "Time tracking for shops — shift clock-in, cover and a payroll-ready export, in one app",
    intro: "Opening shifts, late shifts and someone always covering for someone else. Done-it gives every shop a clean digital record of who worked which shift — clocked in on their own phone, ready for payroll at month-end.",
    pains: [
      {
        title: "Shift swaps never make it to payroll",
        body: "Someone covers a sick colleague, someone stays an hour late at closing. It gets remembered on a note, or not at all — and payroll is wrong before you start.",
      },
      {
        title: "Several shops, one payroll run",
        body: "Staff move between branches, part-timers work across two locations. Collecting hours per shop and merging them by hand costs a day every month.",
      },
      {
        title: "Breaks and overtime are guesswork",
        body: "Statutory breaks get deducted from memory and extra hours are settled informally — the two things an inspection asks about first.",
      },
    ],
    features: [
      {
        title: "Clock in on the shop floor",
        body: "One tap on their own phone at the start of the shift. No terminal at the till, no shared card, no clipboard by the stockroom door.",
      },
      {
        title: "Hours per shop, staff across branches",
        body: "Each shop is a project. Someone covering another branch is clocked against that branch — you see the hours where they were actually worked.",
      },
      {
        title: "Breaks deducted automatically",
        body: "Set the break rule once and it applies to every shift. Overtime is logged the moment it happens, and each employee picks payout or time off.",
      },
    ],
    compliance: {
      label: "Shift-based time registration",
      title: "A defensible record of every shift worked",
      body: "Done-it keeps an objective digital record of every clock-in and clock-out, with breaks and overtime — the record an audit or a payroll dispute asks for.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Staff clock in for their shift", body: "One tap on their phone as the shift starts. Cover for another branch is clocked against that branch." },
      { step: "You see who is in, per shop", body: "A live view across every location — spot an unfilled opening shift before the doors open." },
      { step: "Payroll in one export", body: "Hours, breaks and overtime per employee and per shop, exported ready for your payroll provider." },
    ],
    stats: [
      { value: "1 tap", label: "to clock in — no terminal at the till" },
      { value: "Live", label: "view of who is in, across every shop" },
      { value: "0", label: "shift swaps lost between the floor and payroll" },
    ],
    quote: {
      text: "Two shops and a lot of part-timers. Before, half the cover shifts were settled from memory. Now every hour is on the right shop and payroll takes minutes.",
      who: "Owner, retail · 11 employees",
      result: "Payroll prep from a day to under an hour",
    },
    relatedSlugs: ["hospitality", "beauty", "offices"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "offices",
    name: "Offices & professional services",
    headline: "Time tracking for offices — hours per client and project, without the spreadsheet",
    intro: "Accountants, notaries, architects, agencies and consultancies bill their time — or at least need to know where it went. Done-it records hours against the right client and project, from the desk or on the road.",
    pains: [
      {
        title: "Billable hours reconstructed on Friday",
        body: "Time written down at the end of the week is time guessed. Whole hours quietly disappear between the work and the invoice.",
      },
      {
        title: "No idea what a client actually costs",
        body: "The fee was agreed months ago. Whether the work still fits inside it only becomes clear when someone adds it up — usually too late.",
      },
      {
        title: "Hybrid work makes the record patchy",
        body: "Part of the team is at the office, part at home, part at the client. Attendance stops being a fact and becomes a reconstruction.",
      },
    ],
    features: [
      {
        title: "Hours against client and project",
        body: "Every entry lands on a project, so you can see the hours per client without exporting anything or asking anyone.",
      },
      {
        title: "Works at the desk and off-site",
        body: "The same app on the phone or the browser. Visiting a client counts as work on that client, including the travel if you want it to.",
      },
      {
        title: "Overtime and leave in one place",
        body: "Extra hours logged as they happen, leave requested and approved in the app — no parallel spreadsheet of who is off when.",
      },
    ],
    compliance: {
      label: "Per-client time registration",
      title: "An objective record of hours worked",
      body: "Done-it keeps a digital, tamper-evident record of working time per employee — the basis for both payroll and a defensible client invoice.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Open a client project", body: "Set it up once with the client and, if you bill by budget, the hours you planned." },
      { step: "The team logs time to it", body: "From the desk or the phone. Every hour lands on a client, not in someone's memory." },
      { step: "See hours per client, export for payroll", body: "Live totals per project, and a clean export for payroll or invoicing at month-end." },
    ],
    stats: [
      { value: "Per client", label: "hours, visible while the work is still running" },
      { value: "Desk or road", label: "same record, phone and browser" },
      { value: "1 export", label: "for payroll and invoicing" },
    ],
    quote: {
      text: "We used to rebuild the week every Friday. Now the hours are already on the right client and I can see which files are eating the fee.",
      who: "Partner, accountancy practice · 8 employees",
      result: "Billable hours recovered every month",
    },
    relatedSlugs: ["retail", "healthcare", "wellness"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "beauty",
    name: "Salons & body care",
    headline: "Time tracking for salons — shifts, part-time hours and payroll without the paper diary",
    intro: "Hairdressers, barbers, nail and beauty studios run on shifts and part-time contracts. Done-it records who worked when, deducts breaks automatically and hands payroll a clean file.",
    pains: [
      {
        title: "Part-time contracts, endless variations",
        body: "Three days one week, four the next, a Saturday swapped for a Tuesday. Contracted hours and worked hours drift apart fast.",
      },
      {
        title: "The diary is not a time record",
        body: "Appointments say what was booked, not who worked. When payroll or an inspection asks, the booking system is the wrong answer.",
      },
      {
        title: "Extra hours settled informally",
        body: "Staying late for a last client is remembered, promised, and sometimes forgotten. That is the kind of gap that turns into a dispute.",
      },
    ],
    features: [
      {
        title: "Clock in at the salon",
        body: "One tap on their own phone at the start of the shift — no shared terminal at the desk, no signature list by the mirror.",
      },
      {
        title: "Breaks and part-time rules once",
        body: "Set the break rule and the standard hours once; they apply to every shift, with per-person exceptions where a contract differs.",
      },
      {
        title: "Overtime, paid or taken back",
        body: "Extra hours are logged as they are worked, and each employee chooses payout or time off in lieu — settled in the app, not by memory.",
      },
    ],
    compliance: {
      label: "Digital time registration",
      title: "A record that holds up beyond the appointment book",
      body: "Done-it records actual working time per employee, with breaks and overtime — separate from bookings, and defensible when it is checked.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Staff clock in for the shift", body: "One tap on arrival. Breaks are deducted by the rule you set once." },
      { step: "You see the day as it runs", body: "Who is in, who is on a break, who stayed late — live, without asking." },
      { step: "Payroll gets a clean file", body: "Worked hours, breaks and overtime per employee, exported for your payroll provider." },
    ],
    stats: [
      { value: "1 tap", label: "to clock in — nothing shared, nothing on paper" },
      { value: "Auto", label: "break deduction on every shift" },
      { value: "0", label: "late hours settled from memory" },
    ],
    quote: {
      text: "Everyone is part-time and every week is different. The app just records it — I stopped rebuilding the month in a spreadsheet.",
      who: "Owner, hair salon · 6 employees",
      result: "No more disputed hours at month-end",
    },
    relatedSlugs: ["wellness", "retail", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "wellness",
    name: "Health & wellness",
    headline: "Time tracking for gyms, studios and spas — shifts, classes and payroll in one app",
    intro: "Gyms, fitness studios, physio practices and spas run long opening hours with rotating staff. Done-it records who covered which shift, deducts breaks automatically and exports payroll cleanly.",
    pains: [
      {
        title: "Long opening hours, rotating cover",
        body: "Early shifts, evening classes and weekends, filled by a mix of full-time, part-time and freelance staff. The roster is a plan, not a record.",
      },
      {
        title: "Class hours and floor hours blur",
        body: "An instructor teaches two classes and covers reception in between. Paying that correctly means knowing what was actually worked, not what was scheduled.",
      },
      {
        title: "Several locations, one administration",
        body: "Staff move between studios. Merging their hours by hand each month is slow and error-prone.",
      },
    ],
    features: [
      {
        title: "Clock in per studio",
        body: "One tap on their phone at the site they are working. Cover at another studio lands on that studio's hours.",
      },
      {
        title: "Live view of who is in",
        body: "See the floor across every location in real time — an unfilled early shift is visible before the doors open, not after.",
      },
      {
        title: "Breaks and overtime handled",
        body: "Break rules applied automatically, extra hours logged as they happen with payout or time off in lieu per employee.",
      },
    ],
    compliance: {
      label: "Multi-site shift registration",
      title: "An objective record for every shift and site",
      body: "Done-it keeps a digital record of working time per employee and per location — the basis for correct pay and for any check on hours worked.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Staff clock in at the studio", body: "One tap at the start of the shift, on the site where they are working." },
      { step: "Cover is visible live", body: "Who is in at which location right now, so gaps are filled before members notice." },
      { step: "One export for payroll", body: "Hours, breaks and overtime per employee and per studio, ready for the payroll provider." },
    ],
    stats: [
      { value: "Per studio", label: "hours, even when staff cover elsewhere" },
      { value: "Live", label: "view of the floor across locations" },
      { value: "Auto", label: "breaks and overtime, no manual maths" },
    ],
    quote: {
      text: "Two studios, long opening hours and a lot of cover shifts. Now the hours land on the right site and payroll is one file.",
      who: "Manager, fitness studio · 14 employees",
      result: "Roster and payroll finally agree",
    },
    relatedSlugs: ["beauty", "healthcare", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "hospitality",
    name: "Hospitality",
    headline: "Time tracking for cafés, restaurants and hotels — shifts, breaks and overtime, settled fairly",
    intro: "Split shifts, late closes and a busy Saturday that runs over. Done-it records the hours as they are actually worked, deducts breaks by your rule and gives payroll one clean file.",
    pains: [
      {
        title: "The shift never ends when the roster says",
        body: "Service runs late, closing takes another half hour. Those minutes are real work, and settling them from memory is how disputes start.",
      },
      {
        title: "Split shifts are hard to record",
        body: "Lunch service, a break, then dinner. On paper it becomes one long block or two guesses — neither is the truth.",
      },
      {
        title: "High turnover, constant onboarding",
        body: "New staff every season. Any system that needs training will not survive the first busy week.",
      },
    ],
    features: [
      {
        title: "Clock in and out per service",
        body: "One tap at the start and end of each block, so split shifts are recorded as they happened — not merged into one.",
      },
      {
        title: "Breaks by your rule",
        body: "Mandatory breaks deducted automatically, with exceptions per person where a contract needs it. No manual maths at month-end.",
      },
      {
        title: "Overtime settled in the app",
        body: "The extra half hour after closing is logged the moment it is worked, and paid out or taken back as time off — the employee chooses.",
      },
    ],
    compliance: {
      label: "Split-shift time registration",
      title: "A defensible record of hours in a high-turnover team",
      body: "Done-it keeps an objective digital record of every shift, break and extra hour — which is exactly what a payroll check or a labour inspection asks to see.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Staff clock in for service", body: "One tap at the start of the block; the same at the end. Split shifts stay split." },
      { step: "You see the floor live", body: "Who is in for tonight's service, who is still on a break, who stayed past closing." },
      { step: "Payroll gets the real hours", body: "Worked time, breaks and overtime per employee, exported in one file." },
    ],
    stats: [
      { value: "Split shifts", label: "recorded as two blocks, not one guess" },
      { value: "Auto", label: "break deduction per your rule" },
      { value: "1 min", label: "to onboard a new starter — it is their own phone" },
    ],
    quote: {
      text: "Closing always runs over. Now those minutes are logged instead of argued about, and staff can take them back as time off.",
      who: "Owner, restaurant · 17 employees",
      result: "Fewer disputes, faster payroll",
    },
    relatedSlugs: ["retail", "wellness", "beauty"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "healthcare",
    name: "Healthcare & care",
    headline: "Time tracking for care teams — shifts, home visits and qualifications, in one record",
    intro: "Practices, home-care teams and care homes run around shifts, visits and strict record-keeping. Done-it registers worked time per shift and per visit, and keeps employee documents where you can find them.",
    pains: [
      {
        title: "Visits and shifts in separate systems",
        body: "Home visits live in one tool, rostered hours in another, and payroll gets a third version. Reconciling them is a monthly job.",
      },
      {
        title: "Night and weekend hours priced wrong",
        body: "Unsocial hours carry different rates. When the record is approximate, so is the pay — and the correction comes later.",
      },
      {
        title: "Certificates expire quietly",
        body: "Qualifications and certificates matter in care, and they sit in a folder nobody opens until an inspection asks.",
      },
    ],
    features: [
      {
        title: "Clock in per shift or per visit",
        body: "One tap at the start of the shift, or at each client address for home care — location-verified where you need the proof.",
      },
      {
        title: "Hours split the way you pay them",
        body: "Night, weekend and regular hours are recorded distinctly, so the payroll export already reflects how the work is actually paid.",
      },
      {
        title: "Employee documents in one place",
        body: "Contracts, certificates and IDs stored next to the person they belong to — found in seconds when someone asks for them.",
      },
    ],
    compliance: {
      label: "Shift & visit registration",
      title: "Records that survive an inspection",
      body: "Done-it keeps a digital, objective record of working time per employee — hours, breaks and unsocial-hour splits — alongside the documents that go with each person.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Clock in for the shift or visit", body: "One tap at the site or the client's address, location-verified where proof is needed." },
      { step: "Hours land in the right bucket", body: "Regular, night and weekend hours recorded distinctly as they happen." },
      { step: "Export for payroll", body: "One clean file per period, already split the way you pay it." },
    ],
    stats: [
      { value: "Per visit", label: "or per shift — whichever your team runs on" },
      { value: "Split", label: "night and weekend hours, recorded as worked" },
      { value: "Filed", label: "certificates stored per employee" },
    ],
    quote: {
      text: "Home visits and rostered shifts finally sit in the same record. Payroll stopped being a reconciliation exercise.",
      who: "Coordinator, home-care team · 22 employees",
      result: "One record instead of three",
    },
    relatedSlugs: ["wellness", "offices", "cleaning"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
];
